import {
  Alert,
  Button,
  Card,
  Container,
  Group,
  Menu,
  rem,
  Space,
  Stack,
  Tabs,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown, IconThumbDown, IconThumbUp} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {
  CompanyEntity,
  DispatchRequestEntity,
  dispatchRequestStatus,
  useGetEntityIntegrationHistory
} from "@ots-frontend/ots-data";
import {DispatchRequestDetails} from "./DispatchRequestDetails";
import {useTranslation} from "react-i18next";
import {DispatchRequestJobs} from "./DispatchRequestJobs";
import {
  DispatchRequestProgress,
  fromDispatchRequest
} from "../../progress/DispatchRequestProgress/DispatchRequestProgress";
import {
  DispatchRequestAction,
  DispatchRequestActionMenuDropdown
} from "../../menu/DispatchRequestActionMenu/DispatchRequestActionMenu";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {useDisclosure} from "@mantine/hooks";
import {DispatchRequestReassignModal} from "./DispatchRequestReassignModal";
import {createContentStyles} from "../../../theme/useContentStyles";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {getDispatchRequestStatusColorRef} from "../../../util/dispatch-request-ui-util";
import {useDispatchRequestMutation} from "../../../hooks/useDispatchRequestMutation";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {EntityIntegrationStatusView} from "../common/EntityIntegrationStatusView/EntityIntegrationStatusView";
import {EntityCommentsCard} from "../common/EntityCommentsCard/EntityCommentsCard";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type DispatchRequestProfileProps = PropsWithChildren<{
  dispatchRequest: DispatchRequestEntity;
  showAcceptRejectActions?: boolean;
  showJobViewAction?: boolean;
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "jobs",
    value: "Jobs"
  },
  {
    key: "comments",
    value: "Comments"
  }
];

export const DispatchRequestProfile = (props: DispatchRequestProfileProps) => {
  const {
    dispatchRequest,
    showAcceptRejectActions = true,
    showJobViewAction = true
  } = props;

  const {classes} = useStyles();

  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {t} = useTranslation(["common"])

  const {
    cancelDispatchRequest,
    authorizeDispatchRequest,
    reassignDispatchRequest,
    rejectDispatchRequest,
    isCancelling,
    isAuthorizing,
    isReassigning,
    isRejecting
  } = useDispatchRequestMutation()

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: dispatchRequest
  });

  const cancelDisclosure = useDisclosure(false)
  const authDisclosure = useDisclosure(false)
  const reassignDisclosure = useDisclosure(false)
  const rejectDisclosure = useDisclosure(false)

  const {history} = useGetEntityIntegrationHistory({entity: dispatchRequest});

  const items = useMemo(() => {
    const _tabs: TabItem[] = [...tabs]

    if (history && history.length > 0) {
      _tabs.push({
        key: "integrations",
        value: "Integrations"
      })
    }

    return _tabs.map((tab) => (
      <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
        <Tabs.Tab value={tab.key} icon={tab.icon}>
          {tab.value}
        </Tabs.Tab>
      </HasAccess>
    ))
  }, [history])

  const onAcceptClick = useCallback(() => {
    navigate(`/requests/accept`, {state: {id: dispatchRequest.id}})
  }, [navigate, dispatchRequest.id])

  const onRejectClick = useCallback(() => {
    rejectDisclosure[1].open()
  }, [rejectDisclosure])

  const onCancelSubmit = useCallback((comments: string | undefined) => {
    if (comments) {
      cancelDispatchRequest(dispatchRequest.asTypeValue(), comments, undefined)
    }
  }, [dispatchRequest, cancelDispatchRequest])

  const onAuthSubmit = useCallback((comments: string | undefined) => {
    if (comments) {
      authorizeDispatchRequest(dispatchRequest.asTypeValue(), comments, undefined)
    }
  }, [dispatchRequest, authorizeDispatchRequest])

  const onReassignSubmit = useCallback((company: CompanyEntity, comments: string) => {
    if (comments) {
      reassignDispatchRequest(dispatchRequest.id, company.id, comments, undefined)
    }
  }, [dispatchRequest, reassignDispatchRequest])

  const onRejectSubmit = useCallback((comments: string | undefined) => {
    if (comments) {
      rejectDispatchRequest(dispatchRequest.id, comments, undefined)
      rejectDisclosure[1].close()
    }
  }, [dispatchRequest.id, rejectDisclosure, rejectDispatchRequest])

  const onDispatchRequestActionClick = useCallback((action: DispatchRequestAction) => {
    switch (action) {
      case "COMMENT":
        handlers.open()
        break;
      case "EDIT":
        navigate(`/requests/form`, {state: {id: dispatchRequest.id}})
        break;
      case "CANCEL":
        cancelDisclosure[1].open()
        break;
      case "AUTH":
        authDisclosure[1].open()
        break;
      case "REASSIGN":
        reassignDisclosure[1].open()
        break;
    }
  }, [handlers, navigate, dispatchRequest.id, cancelDisclosure, authDisclosure, reassignDisclosure])

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>
            {t("profile.dispatchrequest.title", "Dispatch Request #{{id}}", {
              id: dispatchRequest.resourceId ?? t("common.shared.unknown")
            })}
          </Title>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <DispatchRequestActionMenuDropdown
                onActionClick={onDispatchRequestActionClick}
                dispatchRequest={dispatchRequest}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Stack>
            <DispatchRequestProgress
              {...fromDispatchRequest(dispatchRequest)}
              mx="md"
              w="25%"
            />
            {dispatchRequest.externalSystem &&
              <Alert title={t("common.entity.dispatchrequest.profile.externalrequest", "External Request")}
                     color="warning">
                {dispatchRequest.externalSystem}
              </Alert>
            }
            {showAcceptRejectActions && dispatchRequest.status === dispatchRequestStatus.pending &&
              <HasAccess permissions={["dispatch_request_accept_reject"]}>
                <Group m="md">
                  <Button
                    onClick={onAcceptClick}
                    leftIcon={<IconThumbUp size="1rem"/>}
                    color={getDispatchRequestStatusColorRef(dispatchRequestStatus.accepted)}>
                    {t("common.action.accept", "Accept")}
                  </Button>
                  <Button
                    onClick={onRejectClick}
                    leftIcon={<IconThumbDown size="1rem"/>}
                    color={getDispatchRequestStatusColorRef(dispatchRequestStatus.rejected)}>
                    {t("common.action.reject", "Reject")}
                  </Button>
                </Group>
              </HasAccess>
            }
            <Tabs
              defaultValue={state?.tabValue ?? "details"}
              onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
              p="sm"
            >
              <Tabs.List>{items}</Tabs.List>
              <Space h="md"/>
              <Tabs.Panel value="details">
                <DispatchRequestDetails dispatchRequest={dispatchRequest}/>
              </Tabs.Panel>
              <Tabs.Panel value="jobs">
                <DispatchRequestJobs
                  dispatchRequest={dispatchRequest}
                  showViewAction={showJobViewAction}/>
              </Tabs.Panel>
              <Tabs.Panel value="comments">
                <EntityCommentsCard entity={dispatchRequest}/>
              </Tabs.Panel>
              {(history && history.length > 0) &&
                <Tabs.Panel value="integrations">
                  <EntityIntegrationStatusView entity={dispatchRequest}/>
                </Tabs.Panel>
              }
            </Tabs>
          </Stack>
        </Container>
      </Card.Section>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>

      <CommentModal
        title={t<string>("profile.dispatchrequest.action.cancel.alert.title", "Cancel")}
        opened={cancelDisclosure[0]}
        onClose={cancelDisclosure[1].close}
        onSubmit={onCancelSubmit}
        loading={isCancelling}
        color="red"
        message={
          <Text size="sm">
            {t("profile.dispatchrequest.action.cancel.alert.message", "Are you sure you want to cancel this dispatch request?")}
          </Text>
        }/>

      <CommentModal
        title={t<string>("profile.dispatchrequest.action.authorize.alert.title", "Authorize")}
        opened={authDisclosure[0]}
        onClose={authDisclosure[1].close}
        onSubmit={onAuthSubmit}
        loading={isAuthorizing}/>

      <CommentModal
        title={t<string>("profile.dispatchrequest.action.reject.alert.title", "Reject")}
        opened={rejectDisclosure[0]}
        onClose={rejectDisclosure[1].close}
        onSubmit={onRejectSubmit}
        loading={isRejecting}
        color="red"
        message={
          <Text size="sm">
            {t("profile.dispatchrequest.action.reject.alert.message", "Are you sure you want to reject this dispatch request?")}
          </Text>
        }/>

      <DispatchRequestReassignModal
        title={t<string>("profile.dispatchrequest.action.reassign.alert.title", "Reassign")}
        opened={reassignDisclosure[0]}
        onClose={reassignDisclosure[1].close}
        onSubmit={onReassignSubmit}
        loading={isReassigning}
        color="red"/>
    </Card>
  );
};
